import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  Space,
  Progress,
  List,
  Avatar,
  Tag,
  Spin
} from 'antd';
import {
  UserOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  BookOutlined,
  TrophyOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { statsService } from '../services/statsService';
import DataInitializer from '../components/DataInitializer';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const statsData = await statsService.getDashboardStats();
      
      const statsArray = [
        {
          title: 'Всего пользователей',
          value: statsData.totalUsers || 0,
          icon: <UserOutlined style={{ color: '#2196F3' }} />,
          change: '+12%',
          color: '#2196F3'
        },
        {
          title: 'Активные игры',
          value: statsData.activeGames || 0,
          icon: <PlayCircleOutlined style={{ color: '#4CAF50' }} />,
          change: '+2',
          color: '#4CAF50'
        },
        {
          title: 'Статьи',
          value: statsData.totalArticles || 0,
          icon: <FileTextOutlined style={{ color: '#FF9800' }} />,
          change: '+3',
          color: '#FF9800'
        },
        {
          title: 'Просмотры',
          value: statsData.totalViews || 0,
          icon: <EyeOutlined style={{ color: '#9C27B0' }} />,
          change: '+8%',
          color: '#9C27B0'
        }
      ];
      
      setStats(statsArray);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Загрузка данных...</div>
      </div>
    );
  }

  const userActivityData = [
    { name: 'Пн', users: 120, games: 45 },
    { name: 'Вт', users: 190, games: 67 },
    { name: 'Ср', users: 300, games: 89 },
    { name: 'Чт', users: 280, games: 78 },
    { name: 'Пт', users: 189, games: 56 },
    { name: 'Сб', users: 239, games: 78 },
    { name: 'Вс', users: 349, games: 98 }
  ];

  const gameStatsData = [
    { name: '2048', value: 35, color: '#2196F3' },
    { name: 'Пазл', value: 25, color: '#4CAF50' },
    { name: 'Тетрис', value: 20, color: '#FF9800' },
    { name: 'Раскраски', value: 15, color: '#9C27B0' },
    { name: 'Память', value: 5, color: '#F44336' }
  ];

  const recentActivities = [
    {
      title: 'Новый пользователь зарегистрирован',
      description: 'Ахмад Саидов',
      time: '2 минуты назад',
      avatar: <Avatar size="small" icon={<UserOutlined />} />
    },
    {
      title: 'Добавлена новая игра',
      description: 'Игра "Найди пары"',
      time: '1 час назад',
      avatar: <Avatar size="small" icon={<PlayCircleOutlined />} />
    },
    {
      title: 'Опубликована статья',
      description: '10 советов по воспитанию детей',
      time: '3 часа назад',
      avatar: <Avatar size="small" icon={<FileTextOutlined />} />
    },
    {
      title: 'Обновлен документ',
      description: 'Конвенция о правах ребенка',
      time: '5 часов назад',
      avatar: <Avatar size="small" icon={<BookOutlined />} />
    }
  ];

  const topGames = [
    { name: '2048', players: 1247, rating: 4.8 },
    { name: 'Пазл', players: 892, rating: 4.6 },
    { name: 'Тетрис', players: 756, rating: 4.7 },
    { name: 'Раскраски', players: 634, rating: 4.5 },
    { name: 'Память', players: 423, rating: 4.3 }
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Дашборд</Title>
        <Text type="secondary">Обзор статистики и активности приложения</Text>
      </div>

      <DataInitializer />

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.icon}
                suffix={
                  <Text style={{ color: stat.color, fontSize: '14px' }}>
                    {stat.change}
                  </Text>
                }
                valueStyle={{ color: stat.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* User Activity Chart */}
        <Col xs={24} lg={16}>
          <Card title="Активность пользователей" style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={userActivityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="users" 
                  stackId="1" 
                  stroke="#2196F3" 
                  fill="#2196F3" 
                  fillOpacity={0.6}
                />
                <Area 
                  type="monotone" 
                  dataKey="games" 
                  stackId="2" 
                  stroke="#4CAF50" 
                  fill="#4CAF50" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Game Statistics */}
        <Col xs={24} lg={8}>
          <Card title="Популярность игр" style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={gameStatsData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {gameStatsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        {/* Recent Activities */}
        <Col xs={24} lg={12}>
          <Card title="Последние активности">
            <List
              dataSource={recentActivities}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={item.avatar}
                    title={item.title}
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{item.description}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.time}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Top Games */}
        <Col xs={24} lg={12}>
          <Card title="Топ игр">
            <List
              dataSource={topGames}
              renderItem={(item, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        style={{ backgroundColor: '#2196F3' }}
                        icon={<TrophyOutlined />}
                      >
                        {index + 1}
                      </Avatar>
                    }
                    title={item.name}
                    description={
                      <Space>
                        <Text type="secondary">{item.players} игроков</Text>
                        <Tag color="gold">{item.rating} ⭐</Tag>
                      </Space>
                    }
                  />
                  <Progress 
                    percent={item.players / 15} 
                    size="small" 
                    showInfo={false}
                    strokeColor="#2196F3"
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
